import React, { useEffect, useMemo, useState } from 'react';
import { getUsersOrderActions } from '../../app/api/useDataManage/userActions';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import DashboardWrapper from './components/DashboardWrapper';
import CustomLink from '../CustomLink';
import { useTranslation } from 'react-i18next';

const PRODUCT_LABELS = {
  riise_formula: 'Riise',
  'omega-3': 'Omega-3',
};

function formatCurrency(value) {
  const numericValue = Number(value || 0);
  return `€${numericValue.toFixed(2)}`;
}

function getStatusClasses(status) {
  const normalizedStatus = String(status || '').toLowerCase();

  if (['delivered', 'completed'].includes(normalizedStatus)) {
    return 't-bg-emerald-100 t-text-emerald-700';
  }

  if (['shipped', 'in_transit', 'processing'].includes(normalizedStatus)) {
    return 't-bg-sky-100 t-text-sky-700';
  }

  if (['cancelled', 'canceled', 'failed'].includes(normalizedStatus)) {
    return 't-bg-rose-100 t-text-rose-700';
  }

  return 't-bg-amber-100 t-text-amber-700';
}

function getOrderItems(order) {
  if (Array.isArray(order?.order_items) && order.order_items.length > 0) {
    return order.order_items;
  }

  if (!order) {
    return [];
  }

  return [
    {
      line_item: 1,
      product_type: order.product_type,
      product_name: order.productName || PRODUCT_LABELS[order.product_type] || 'Product',
      price: order.discounted_price || order.totalPrice || 0,
    },
  ];
}

function CustomerOrder() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [token] = useState(localStorage.getItem('customer_token'));
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const { userOrders, isOrdersLoading } = useSelector((state) => state.user);
  const { t } = useTranslation();

  useEffect(() => {
    dispatch(getUsersOrderActions(token));
  }, [dispatch, token]);

  const normalizedOrders = useMemo(
    () =>
      (userOrders || []).map((order) => ({
        ...order,
        resolvedItems: getOrderItems(order),
      })),
    [userOrders],
  );
  console.log('userOrders -> ', userOrders);

  const handleViewDetails = (orderData) => {
    navigate('/dashboard/orderDetails', { state: orderData });
  };

  const toggleOrder = (orderId) => {
    setExpandedOrderId((current) => (current === orderId ? null : orderId));
  };

  return (
    <DashboardWrapper title={t('CustomerOrders_Title')}>
      <div className="col-xl-8 col-lg-8 user-inner t-min-w-0 t-w-full">
        <div className="profile-reviews-wrapper t-min-w-0">
          <div className="t-min-w-0 t-w-full">
            <div className="welcome t-mb-6">
              <h4 className="t-font-bold">{t('CustomerOrders_Heading')}</h4>
            </div>

            {isOrdersLoading ? (
              <div className="t-rounded-lg t-border t-border-gray-200 t-bg-white t-p-6 t-text-sm t-text-gray-500">
                {t('CustomerOrders_Loading')}
              </div>
            ) : normalizedOrders.length > 0 ? (
              <div className="t-overflow-hidden t-rounded-lg t-border t-border-gray-200 t-bg-white t-shadow-[0_8px_24px_rgba(15,23,42,0.05)]">
                <div className="t-hidden md:t-grid md:t-grid-cols-[0.8fr_1fr_0.9fr_1.1fr_0.8fr_1.15fr] t-gap-3 t-bg-gray-50 t-px-4 t-py-3 t-text-center t-text-xs t-font-semibold t-uppercase t-tracking-[0.12em] t-text-gray-500">
                  <span>{t('CustomerOrders_Col_Order')}</span>
                  <span>{t('CustomerOrders_Col_InvoiceDate')}</span>
                  <span>{t('CustomerOrders_Col_Status')}</span>
                  <span>{t('CustomerOrders_Col_Tracking')}</span>
                  <span>{t('CustomerOrders_Items')}</span>
                  <span>{t('CustomerOrders_Col_Actions')}</span>
                </div>

                <div>
                  {normalizedOrders.map((order) => {
                    const isExpanded = expandedOrderId === order.id;
                    const displayDate = order.invoice_date || order.createdAt;

                    return (
                      <div key={order.id} className="t-border-t t-border-gray-200 first:t-border-t-0">
                        <div
                          className="t-grid t-grid-cols-2 t-gap-3 t-bg-white t-px-4 t-py-4 t-transition hover:t-cursor-pointer hover:t-bg-gray-50 md:t-grid-cols-[0.8fr_1fr_0.9fr_1.1fr_0.8fr_1.15fr] md:t-items-center md:t-text-center"
                          onClick={() => toggleOrder(order.id)}
                        >
                          <div className="md:t-flex md:t-flex-col md:t-items-center">
                            <p className="t-mb-1 t-text-[11px] t-font-semibold t-uppercase t-tracking-[0.12em] t-text-gray-500 md:t-hidden">
                              {t('CustomerOrders_Col_Order')}
                            </p>
                            <span className="t-text-sm t-font-semibold t-text-gray-900">#{order.id}</span>
                          </div>

                          <div className="md:t-flex md:t-flex-col md:t-items-center">
                            <p className="t-mb-1 t-text-[11px] t-font-semibold t-uppercase t-tracking-[0.12em] t-text-gray-500 md:t-hidden">
                              {t('CustomerOrders_Col_InvoiceDate')}
                            </p>
                            <span className="t-text-sm t-text-gray-700">
                              {displayDate ? new Date(displayDate).toLocaleDateString() : '-'}
                            </span>
                          </div>

                          <div className="md:t-flex md:t-flex-col md:t-items-center">
                            <p className="t-mb-1 t-text-[11px] t-font-semibold t-uppercase t-tracking-[0.12em] t-text-gray-500 md:t-hidden">
                              {t('CustomerOrders_Col_Status')}
                            </p>
                            <span
                              className={`t-inline-flex t-rounded-full t-px-3 t-py-1 t-text-xs t-font-semibold t-capitalize ${getStatusClasses(order.status)}`}
                            >
                              {order.status || '-'}
                            </span>
                          </div>

                          <div className="md:t-flex md:t-flex-col md:t-items-center">
                            <p className="t-mb-1 t-text-[11px] t-font-semibold t-uppercase t-tracking-[0.12em] t-text-gray-500 md:t-hidden">
                              {t('CustomerOrders_Col_Tracking')}
                            </p>
                            {order?.details?.includes('https') ? (
                              <CustomLink to={order.details} target="_blank">
                                {t('CustomerOrders_TrackingLink')}
                              </CustomLink>
                            ) : (
                              <span className="t-text-sm t-text-gray-700">-</span>
                            )}
                          </div>

                          <div className="md:t-flex md:t-flex-col md:t-items-center">
                            <p className="t-mb-1 t-text-[11px] t-font-semibold t-uppercase t-tracking-[0.12em] t-text-gray-500 md:t-hidden">
                              {t('CustomerOrders_Items')}
                            </p>
                            <span className="t-text-sm t-font-medium t-text-gray-900">
                              {order.items_count || order.resolvedItems.length}
                            </span>
                          </div>

                          <div className="t-col-span-2 t-flex t-flex-wrap t-items-center t-justify-end t-gap-2 md:t-col-span-1 md:t-justify-center">
                            <button
                              type="button"
                              className="t-inline-flex t-items-center t-justify-center t-rounded-full t-bg-secondary t-px-4 t-py-2 t-text-sm t-font-semibold t-text-white t-transition hover:t-opacity-90"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleViewDetails(order);
                              }}
                            >
                              {t('CustomerOrders_View')}
                            </button>
                          </div>
                        </div>

                        {isExpanded ? (
                          <div className="t-border-t t-border-gray-200 t-bg-gray-50 t-px-4 t-py-3">
                            <div className="t-mb-3 t-flex t-items-center t-justify-between t-gap-3 t-flex-wrap">
                              <h5 className="t-mb-0 t-text-sm t-font-semibold t-text-gray-900">
                                {t('CustomerOrders_Items')}
                              </h5>
                              <span className="t-text-xs t-text-gray-500">
                                {order.items_count || order.resolvedItems.length}{' '}
                                {t('CustomerOrders_ItemCountSuffix')}
                              </span>
                            </div>
                            <div className="t-space-y-2">
                              {order.resolvedItems.map((item) => (
                                <div
                                  key={`${order.id}-${item.line_item || item.product_type}`}
                                  className="t-flex t-items-center t-justify-between t-gap-4 t-rounded-xl t-border t-border-gray-200 t-bg-white t-px-3 t-py-3"
                                >
                                  <div>
                                    <p className="t-mb-1 t-text-sm t-font-semibold t-text-gray-900">
                                      {item.product_name ||
                                        PRODUCT_LABELS[item.product_type] ||
                                        item.product_type}
                                    </p>
                                    <p className="t-mb-0 t-text-xs t-uppercase t-tracking-[0.12em] t-text-gray-500">
                                      {PRODUCT_LABELS[item.product_type] || item.product_type}
                                    </p>
                                  </div>
                                  <span className="t-text-sm t-font-semibold t-text-gray-900">
                                    {formatCurrency(item.price)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <p>{t('CustomerOrders_Empty')}</p>
            )}
          </div>
        </div>
        <div className="blog-detail-inner"></div>
      </div>
    </DashboardWrapper>
  );
}

export default CustomerOrder;
